import { useCallback, useMemo, useState } from "react";
import { ListProductUseCase } from "../../../Usecase/Products/list-product-usecase";
import { useToast } from "../../../Hooks/ToasContext";
import { CreateCubagesUseCase } from "../../../Usecase/Cubages/create-cubages-usecase";
import { ListCubagesUseCase } from "../../../Usecase/Cubages/list-cubages-usecase";
import { ICubageEntity, IUpdateCubageProductPayload } from "../../../Services/api/cubages/cubages.interface";
import { CreateCubagesVariationsUseCase } from "../../../Usecase/CubagesVariations/create-cubages-variations-usecase";
import { ListBoxesUseCase } from "../../../Usecase/Boxes/list-boxes-usecase";
import { IOptionsProps } from "../../../Components/Form/Select/select.component";
import { ICreateCubageVariantPayload, ICubageVariantEntity, IUpdateCubageVariantPayload } from "../../../Services/api/cubages-variants/cubages-variants.interface";
import { ShowCubagesVariationsUseCase } from "../../../Usecase/CubagesVariations/show-cubages-variations-usecase";
import { UpdateubagesVariationsUseCase } from "../../../Usecase/CubagesVariations/update-cubages-variations-usecase";
import { DestroyCubagesVariationsUseCase } from "../../../Usecase/CubagesVariations/destroy-cubages-variations-usecase";
import { ShowCubagesUseCase } from "../../../Usecase/Cubages/show-cubages-usecase";
import { UpdateCubagesUseCase } from "../../../Usecase/Cubages/update-cubages-usecase";
import { DestroyCubagesUseCase } from "../../../Usecase/Cubages/destroy-cubages-usecase";

interface IUseCubagesProps {
  listProductUseCase: ListProductUseCase
  createCubagesUseCase: CreateCubagesUseCase
  listCubagesUseCase: ListCubagesUseCase
  createCubagesVariationsUseCase: CreateCubagesVariationsUseCase
  listBoxesUseCase: ListBoxesUseCase
  showCubagesVariationsUseCase: ShowCubagesVariationsUseCase
  updateubagesVariationsUseCase: UpdateubagesVariationsUseCase
  destroyCubagesVariationsUseCase: DestroyCubagesVariationsUseCase
  showCubagesUseCase: ShowCubagesUseCase
  updateCubagesUseCase: UpdateCubagesUseCase
  destroyCubagesUseCase: DestroyCubagesUseCase
}

export function useCubages({
  createCubagesUseCase,
  listCubagesUseCase,
  createCubagesVariationsUseCase,
  listBoxesUseCase,
  updateubagesVariationsUseCase,
  destroyCubagesVariationsUseCase,
  showCubagesUseCase,
  updateCubagesUseCase,
  destroyCubagesUseCase,
}: IUseCubagesProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [data, setData] = useState<ICubageEntity[]>([])
    const [showNewCubage, setShowNewCubage] = useState(false)
    const [cubageProductIdSelected, setCubageProductIdSelected] = useState('')
    const [boxOptions, setBoxOptions] = useState<IOptionsProps[]>()
    const [cubageVariantSelected, setCubageVariantSelected] = useState<ICubageVariantEntity | undefined>(undefined)
    const [cubageVariantToDestroy, setCubageVariantToDestroy] = useState('')
    const [variantProductSelected, setVariantProductSelected] = useState<ICubageEntity | undefined>(undefined)
    const [variantProductToDestroy, setVariantProductToDestroy] = useState<string>('')

    const { showToast } = useToast()

    const fetchCubages = useCallback(async () => {
      const listResult = await listCubagesUseCase.handle(1);

      if (listResult.isFailure) {
        setLoadError(true)
        showToast(String(listResult?.error?.error), 'error');
        return false;
      }

      const list = listResult.getValue()
      if (list) {
        setData(list)
        setLoadError(false)
      }

      return true;
    }, [listCubagesUseCase, showToast]);

    const listCubages = useCallback(async () => {
        try {
          setLoading(true)
          setLoadError(false)
          await fetchCubages()
        } catch (error) {
          setLoadError(true)
          console.error("error Cubages function: ", error)
        } finally {
          setLoading(false);
        }
    }, [fetchCubages]);

    const listBoxes = useCallback(async () => {
      try {
        const listResult = await listBoxesUseCase.handle(1);

        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

       const list = listResult.getValue()

       if(list){
          setBoxOptions(list.map((item) => ({
            value: item.id,
            label: `${item.code} — ${item.length}×${item.width}×${item.height} cm`,
          })))
       }
      } catch (error) {
        console.error("error listBoxes function: ", error)
      }
    }, [listBoxesUseCase, showToast]);

    const loadInitialData = useCallback(async () => {
      setLoading(true)
      setLoadError(false)
      try {
        const [cubagesLoaded] = await Promise.all([fetchCubages(), listBoxes()])
        if (!cubagesLoaded) {
          setLoadError(true)
        }
      } catch (error) {
        setLoadError(true)
        console.error("error loadInitialData function: ", error)
      } finally {
        setLoading(false)
      }
    }, [fetchCubages, listBoxes]);

    const list = useMemo(() => {
        return data?.map((product) => ({
            id: product.id,
            name: product.name,
            variants: product.variants ?? [],
        })) ?? []
    }, [data])

    const handleCreateCubage = async (name: string) => {
      try {
        setLoading(true)

        const createResult = await createCubagesUseCase.handle(name);

        if (createResult.isFailure) {
          showToast(String(createResult?.error?.error), 'error');
          return;
        }

        showToast('Cadastrado com sucesso', 'success');
        setShowNewCubage(false)
        await listCubages()
      } catch (error) {
        console.log("error create Cubages function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleCreateCubageVariation = async (data: ICreateCubageVariantPayload) => {
      try {
        setLoading(true)

        const createResult = await createCubagesVariationsUseCase.handle(data);

        if (createResult.isFailure) {
          showToast(String(createResult?.error?.error), 'error');
          return;
        }

        showToast('Cadastrado com sucesso', 'success');
        setShowNewCubage(false)
        setCubageProductIdSelected('')
        setCubageVariantSelected(undefined)
        await listCubages()
      } catch (error) {
        console.log("error create Cubages function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateCubageVariation = async (data: IUpdateCubageVariantPayload) => {
      try {
        setLoading(true)

        const updateResult = await updateubagesVariationsUseCase.handle(data);

        if (updateResult.isFailure) {
          showToast(String(updateResult?.error?.error), 'error');
          return;
        }

        showToast('Editado com sucesso', 'success');
        setCubageProductIdSelected('')
        setCubageVariantSelected(undefined)
        await listCubages()
      } catch (error) {
        console.log("error update Cubages function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleDestroyVariant = async (variationId: string) => {
      try {
        setLoading(true)

        const destroyResult = await destroyCubagesVariationsUseCase.handle(variationId);

        if (destroyResult.isFailure) {
          showToast(String(destroyResult?.error?.error), 'error');
          return;
        }

        showToast('Removido com sucesso', 'success');
        setCubageVariantToDestroy('')
        await listCubages()
      } catch (error) {
        console.log("error destroy Cubages function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleShowCubage = async (cubageId: string) => {
      try {
        setLoading(true)

        const showResult = await showCubagesUseCase.handle(cubageId);

        if (showResult.isFailure) {
          showToast(String(showResult?.error?.error), 'error');
          return;
        }

        const result = showResult.getValue()

       if(result?.id) {
        setVariantProductSelected(result)
       }
      } catch (error) {
        console.log("error show Cubages function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateCubage = async (data: IUpdateCubageProductPayload) => {
      try {
        setLoading(true)

        const updateResult = await updateCubagesUseCase.handle(data);

        if (updateResult.isFailure) {
          showToast(String(updateResult?.error?.error), 'error');
          return;
        }

        showToast('Atualizado com sucesso', 'success');
        setVariantProductSelected(undefined)
        await listCubages()
      } catch (error) {
        console.log("error update Cubages function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleDestroyCubage = async (cubageId: string) => {
      try {
        setLoading(true)

        const destroyResult = await destroyCubagesUseCase.handle(cubageId);

        if (destroyResult.isFailure) {
          showToast(String(destroyResult?.error?.error), 'error');
          return;
        }

        showToast('Removido com sucesso', 'success');
        setVariantProductToDestroy('')
        await listCubages()
      } catch (error) {
        console.log("error destroy Cubages function: ", error)
      } finally {
        setLoading(false);
      }
    };

    return {
        loading,
        loadError,
        listCubages,
        list,
        data,
        showNewCubage,
        setShowNewCubage,
        handleCreateCubage,
        handleCreateCubageVariation,
        setCubageProductIdSelected,
        cubageProductIdSelected,
        listBoxes,
        boxOptions,
        setCubageVariantSelected,
        cubageVariantSelected,
        handleUpdateCubageVariation,
        cubageVariantToDestroy,
        setCubageVariantToDestroy,
        handleDestroyVariant,
        variantProductSelected,
        setVariantProductSelected,
        variantProductToDestroy,
        setVariantProductToDestroy,
        handleShowCubage,
        handleUpdateCubage,
        handleDestroyCubage,
        loadInitialData,
    }
}
